// import { UserPermission } from 'ts-back-end/src/business/user/dto/create-user.dto'

export enum UserPermission {
  EDIT_PHYSICIAN_OFFICES = 'EDIT_PHYSICIAN_OFFICES',
  EDIT_PHYSICIANS = 'EDIT_PHYSICIANS',
  EDIT_OFFICES = 'EDIT_OFFICES',
  EDIT_REPORTS = 'EDIT_REPORTS',
  MODIFY_TEST_CONDITIONS = 'MODIFY_TEST_CONDITIONS',
  MOVE_REPORTS = 'MOVE_REPORTS',
  DISREGARD_REPORTS = 'DISREGARD_REPORTS',
  WIPE_REPORT_DATA = 'WIPE_REPORT_DATA',
  MODIFY_INSURANCE = 'MODIFY_INSURANCE',
  MANAGE_DEVICES = 'MANAGE_DEVICES',
  MANAGE_DEVICE_INVENTORY = 'MANAGE_DEVICE_INVENTORY',
  OVERRIDE_DEVICE_SHELF_LIFE = 'OVERRIDE_DEVICE_SHELF_LIFE',
  EDIT_TEMPLATES = 'EDIT_TEMPLATES',
  DELETE_INCOMING_FAXES = 'DELETE_INCOMING_FAXES',
  CANCEL_ORDERS = 'CANCEL_ORDERS',
  MARK_MESSAGES_AS_READ = 'MARK_MESSAGES_AS_READ',
  INITIATE_ONESHIP = 'INITIATE_ONESHIP',

  SUBMIT_BRIGHTREE_ORDERS = 'SUBMIT_BRIGHTREE_ORDERS',

  EDIT_DME_USERS = 'EDIT_DME_USERS',
  EDIT_LAB_USERS = 'EDIT_LAB_USERS',
  EDIT_INSURANCE_PAYERS = 'EDIT_INSURANCE_PAYERS',
  EDIT_INS_AUTHORIZATION = 'EDIT_INS_AUTHORIZATION',
  MANAGE_RETELL_OUTBOUND_SETTINGS = 'MANAGE_RETELL_OUTBOUND_SETTINGS',
  MERGE_DME_OFFICES = 'MERGE_DME_OFFICES',
  EDIT_CATALOG = 'EDIT_CATALOG',

  MOVE_PATIENT = 'MOVE_PATIENT',
  MODIFY_PATIENT_INSURANCE_LINK = 'MODIFY_PATIENT_INSURANCE_LINK',
  MODIFY_INSURANCE_ELIGIBILITY_FLAGS = 'MODIFY_INSURANCE_ELIGIBILITY_FLAGS',

  VIEW_TEAM_PRODUCTIVITY_REPORTS = 'VIEW_TEAM_PRODUCTIVITY_REPORTS',
  VIEW_ANALYTICS = 'VIEW_ANALYTICS',
  // Billing analytics (revenue, AR, payer-level financial data) is granted independently of
  // the wider operational analytics VIEW_ANALYTICS covers.
  VIEW_BILLING_ANALYTICS = 'VIEW_BILLING_ANALYTICS',

  ACCESS_BULLETINS = 'ACCESS_BULLETINS',
  MANAGE_TAGS = 'MANAGE_TAGS',

  ADD_READER_APP_VERSION = 'ADD_READER_APP_VERSION',
  REMOVE_READER_APP_VERSION = 'REMOVE_READER_APP_VERSION',

  ADD_BILLING_CHARGES = 'ADD_BILLING_CHARGES',
  MODIFY_BILLING_CHARGES = 'MODIFY_BILLING_CHARGES',
  SUBMIT_BILLING_CHARGES = 'SUBMIT_BILLING_CHARGES',
  REMOVE_BILLING_CHARGES = 'REMOVE_BILLING_CHARGES',
  POST_INSURANCE_PAYMENTS = 'POST_INSURANCE_PAYMENTS',
  POST_PATIENT_PAYMENTS = 'POST_PATIENT_PAYMENTS',
  EDIT_PATIENT_PREPAYMENTS = 'EDIT_PATIENT_PREPAYMENTS',
  WRITE_OFF_PATIENT_BALANCE = 'WRITE_OFF_PATIENT_BALANCE',
  MANAGE_BILLING_SETTINGS = 'MANAGE_BILLING_SETTINGS',
}

export type PermissionItem = { name: string; code: UserPermission }

export type PermissionSection = PermissionItem[] | Record<string, PermissionItem[]>

export const permissions: Record<string, PermissionSection> = {
  users: [
    {
      name: 'Add/Update DME users',
      code: UserPermission.EDIT_DME_USERS,
    },
    // Managing LAB users is now gated by the `is_superadmin` flag, not this permission.
  ],
  physician: [
    {
      name: 'Add/Update physician office',
      code: UserPermission.EDIT_PHYSICIAN_OFFICES,
    },
    { name: 'Update physician name', code: UserPermission.EDIT_PHYSICIANS },
  ],
  insurance: [
    {
      name: 'Add/Update insurance payers',
      code: UserPermission.EDIT_INSURANCE_PAYERS,
    },
    {
      name: 'Add/Update insurance authorization',
      code: UserPermission.EDIT_INS_AUTHORIZATION,
    },
    {
      name: 'Modify link for insurance in patient account',
      code: UserPermission.MODIFY_PATIENT_INSURANCE_LINK,
    },
    {
      name: 'Modify Medicare Advantage / Medicaid flags (controls outsource access)',
      code: UserPermission.MODIFY_INSURANCE_ELIGIBILITY_FLAGS,
    },
  ],
  patient: [
    {
      name: 'Move patient',
      code: UserPermission.MOVE_PATIENT,
    },
  ],
  offices: [
    { name: 'Add/Update office', code: UserPermission.EDIT_OFFICES },
    { name: 'Merge DME offices', code: UserPermission.MERGE_DME_OFFICES },
  ],
  orders: [
    {
      name: 'Add/Update patient insurance during billing review',
      code: UserPermission.MODIFY_INSURANCE,
    },
    { name: 'Cancel order', code: UserPermission.CANCEL_ORDERS },
    { name: 'Mark direct message as read', code: UserPermission.MARK_MESSAGES_AS_READ },
    { name: 'Initiate OneShip (switch order to OneShip)', code: UserPermission.INITIATE_ONESHIP },
  ],
  billing: {
    charges: [
      { name: 'Add charge', code: UserPermission.ADD_BILLING_CHARGES },
      { name: 'Modify charge', code: UserPermission.MODIFY_BILLING_CHARGES },
      { name: 'Remove charge', code: UserPermission.REMOVE_BILLING_CHARGES },
      { name: 'Submit charge', code: UserPermission.SUBMIT_BILLING_CHARGES },
    ],
    payments: [
      { name: 'Post insurance payments', code: UserPermission.POST_INSURANCE_PAYMENTS },
      { name: 'Post patient payments', code: UserPermission.POST_PATIENT_PAYMENTS },
      { name: 'Edit Pre-Payments', code: UserPermission.EDIT_PATIENT_PREPAYMENTS },
      { name: 'Write off patient balance', code: UserPermission.WRITE_OFF_PATIENT_BALANCE },
    ],
    analytics: [
      { name: 'View billing analytics dashboard', code: UserPermission.VIEW_BILLING_ANALYTICS },
    ],
  },
  reports: [
    { name: 'Modify report', code: UserPermission.EDIT_REPORTS },
    { name: 'Update test conditions', code: UserPermission.MODIFY_TEST_CONDITIONS },
    { name: 'Move report', code: UserPermission.MOVE_REPORTS },
    { name: 'Disregard report', code: UserPermission.DISREGARD_REPORTS },
    { name: 'Wipe report', code: UserPermission.WIPE_REPORT_DATA },
  ],
  devices: [
    { name: 'Add/Update device', code: UserPermission.MANAGE_DEVICES },
    { name: 'Assign device to office', code: UserPermission.MANAGE_DEVICE_INVENTORY },
    {
      name: 'Override shelf life (set First Check-Out Date, take out of Retired)',
      code: UserPermission.OVERRIDE_DEVICE_SHELF_LIFE,
    },
  ],
  templates: [
    {
      name: 'Add/Update template and canned responses',
      code: UserPermission.EDIT_TEMPLATES,
    },
  ],
  faxes: [{ name: 'Delete incoming fax', code: UserPermission.DELETE_INCOMING_FAXES }],
  settings: [
    {
      name: 'Manage Retell outbound settings',
      code: UserPermission.MANAGE_RETELL_OUTBOUND_SETTINGS,
    },
    {
      name: 'Manage billing settings',
      code: UserPermission.MANAGE_BILLING_SETTINGS,
    },
  ],
  teamProductivity: [
    {
      name: 'View team productivity reports',
      code: UserPermission.VIEW_TEAM_PRODUCTIVITY_REPORTS,
    },
  ],
  analytics: [
    {
      name: 'View analytics dashboards',
      code: UserPermission.VIEW_ANALYTICS,
    },
  ],
  catalog: [
    {
      name: 'Add/Update/Remove catalog items',
      code: UserPermission.EDIT_CATALOG,
    },
  ],
  bulletins: [
    {
      name: 'Access Bulletins',
      code: UserPermission.ACCESS_BULLETINS,
    },
  ],
  tags: [
    {
      name: 'Manage Tags',
      code: UserPermission.MANAGE_TAGS,
    },
  ],
  readerApp: [
    {
      name: 'Add version',
      code: UserPermission.ADD_READER_APP_VERSION,
    },
    {
      name: 'Remove version',
      code: UserPermission.REMOVE_READER_APP_VERSION,
    },
  ],
}

/** Permissions that are enforced in the front-end. Unimplemented ones appear faded in the user profile checklist. */
export const IMPLEMENTED_PERMISSIONS: Set<UserPermission> = new Set([
  UserPermission.EDIT_DME_USERS,
  UserPermission.EDIT_OFFICES,
  UserPermission.MOVE_PATIENT,
  UserPermission.MODIFY_PATIENT_INSURANCE_LINK,
  UserPermission.CANCEL_ORDERS,
  UserPermission.MARK_MESSAGES_AS_READ,
  UserPermission.INITIATE_ONESHIP,
  UserPermission.MANAGE_DEVICES,
  UserPermission.MANAGE_DEVICE_INVENTORY,
  UserPermission.OVERRIDE_DEVICE_SHELF_LIFE,
  UserPermission.DELETE_INCOMING_FAXES,
  UserPermission.EDIT_INSURANCE_PAYERS,
  UserPermission.MANAGE_RETELL_OUTBOUND_SETTINGS,
  UserPermission.MERGE_DME_OFFICES,
  UserPermission.VIEW_TEAM_PRODUCTIVITY_REPORTS,
  UserPermission.VIEW_ANALYTICS,
  UserPermission.VIEW_BILLING_ANALYTICS,
  UserPermission.EDIT_CATALOG,
  // TES-2881: read-only view mode now enforces these two, plus Bulletins/Tags access.
  UserPermission.EDIT_PHYSICIAN_OFFICES,
  UserPermission.EDIT_PHYSICIANS,
  UserPermission.ACCESS_BULLETINS,
  UserPermission.MANAGE_TAGS,
  UserPermission.ADD_READER_APP_VERSION,
  UserPermission.REMOVE_READER_APP_VERSION,
  UserPermission.ADD_BILLING_CHARGES,
  UserPermission.MODIFY_BILLING_CHARGES,
  UserPermission.SUBMIT_BILLING_CHARGES,
  UserPermission.REMOVE_BILLING_CHARGES,
  UserPermission.POST_INSURANCE_PAYMENTS,
  UserPermission.POST_PATIENT_PAYMENTS,
  UserPermission.EDIT_PATIENT_PREPAYMENTS,
  UserPermission.WRITE_OFF_PATIENT_BALANCE,
  UserPermission.MANAGE_BILLING_SETTINGS,
  UserPermission.EDIT_REPORTS,
  UserPermission.MOVE_REPORTS,
  UserPermission.DISREGARD_REPORTS,
  UserPermission.WIPE_REPORT_DATA,
])

export const sectionPermissions = (section: PermissionSection): PermissionItem[] =>
  Array.isArray(section) ? section : Object.values(section).flat()
